// Re-predicting as the user answers. Two refreshes, both replacing ONLY answers the user has
// not set (refinePrediction) -- nothing the user set is ever changed:
//
//  KEY REFRESH -- the user changes the order type: the rest of the first page still untouched
//    is re-predicted from the best match on it, because a different kind of order makes the
//    rest of the first page wrong too. (npm run predict:legacy-backtest: first page 75% -> 86%
//    right on ordinary orders.) Housing, size and design no longer count here: they are never
//    predicted at launch, so the user always sets them -- and that is what the page-2
//    prediction is for.
//
//  PAGE-2 PREDICTION -- PAGE 1 FIRST (Kevin, 2026-10-17: "when they are ready they will select
//    and you can do the prediction for fire tube"). The launch fills the first page only and
//    leaves the housing to the user (util/prediction launchKeys). Once the housing is chosen,
//    the pages after the first are predicted from the configuration whose first page matches
//    the user's best, housing included, in the background before the user gets there; a later
//    change on the first page predicts them again. Only answers the user never edited are
//    replaced (by key -- the operations set page-2 defaults from the housing meanwhile, and
//    those are the prediction's to replace). Leaving the first page waits for it
//    (Configurator awaits `leaveRef`, bounded), so the next page is drawn with it rather than
//    changing in front of the user. Frozen for good once a later page has been shown.
//
//  Neither runs over an MB burner mirrored from its sibling while it is still the same burner
//  (MIRROR_GUARD below): the mirror is right far more often than any past configuration. Nor
//  over a configuration the rep picked from the Recent menu (prediction.held): that was chosen
//  on purpose, so a changed heat duty must not swap its page 2 for a history guess.

import { useEffect, useMemo, useRef, type MutableRefObject } from "react";
import type { AnswerState, Question, Tab } from "../types";
import { useAnswers } from "../state/AnswersContext";
import { getLaunchContext } from "../util/launchContext";
import { fetchRefinePrediction, toInput, toSource } from "../util/predictionClient";
import { housingChosen, keysOnTab, launchKeys, pageAnswers, visibleTabsInOrder } from "../util/prediction";
import { currentFieldwise, describeFieldwise, PO_READY_EVENT, predictLaterPages } from "../util/fieldwiseRun";
import { toAnswers } from "../util/fieldwise";

const DEBOUNCE_MS = 700;
const KEY_DEBOUNCE_MS = 400;
// The answer that says what kind of order this is. (api/shared/predict/cpq2.js KEY_ANSWERS also
// has housing, size and design -- the user's own choice now, see PAGE 1 FIRST above.)
const KEY_QUESTIONS = new Set(["ORDER_TYPE"]);
const MODEL_KEYS = new Set(["HOUSING", "DIMENSIONS", "DESIGN", "BURNER_QTY"]);
const questionOf = (k: string) => (k.indexOf("/") < 0 ? k : k.slice(0, k.indexOf("/")));
const isKeyAnswer = (k: string) => KEY_QUESTIONS.has(questionOf(k));
// An MB burner mirrored from its sibling stands while these still match the sibling
// (api/shared/predict/mirror.js MIRROR_GUARD): nothing is re-predicted over it. A different
// size or heat duty is a different burner, so then both refreshes run as usual, from history.
const MIRROR_GUARD = new Set(["ORDER_TYPE", "HOUSING", "DIMENSIONS", "DESIGN", "REQUIRED_HEAT_DUTY", "BURNER_QTY"]);
const isGuardAnswer = (k: string) => MIRROR_GUARD.has(questionOf(k));
const filled = (v: unknown) => v !== null && v !== undefined && v !== "" && v !== false;
const signatureOf = (a: AnswerState) =>
  JSON.stringify(Object.keys(a).sort().filter((k) => filled(a[k])).map((k) => [k, a[k]]));

export default function PredictionRefresh({
  questions,
  tabs,
  activeTabId,
  leaveRef,
}: {
  questions: Question[];
  tabs: Tab[];
  activeTabId: string | undefined;
  /** Set here, awaited by Configurator before it leaves the first page. */
  leaveRef: MutableRefObject<(() => Promise<void>) | null>;
}) {
  const { answers, prediction, isPredicted, wasPredicted, refinePrediction, product } = useAnswers();
  const answersLive = useRef(answers);
  answersLive.current = answers;
  const ordered = useMemo(() => visibleTabsInOrder(tabs), [tabs]);
  const firstTabId = ordered[0]?.id;
  const onFirstPage = useMemo(() => (firstTabId ? keysOnTab(questions, firstTabId) : () => false), [questions, firstTabId]);
  // What a launch predicts: the first page minus the housing choice.
  const predictable = useMemo(() => launchKeys(questions, tabs), [questions, tabs]);
  const chosen = housingChosen(answers);
  const single = ordered.length < 2;
  // The later pages are predicted while a prediction exists and is not held -- shown or not:
  // in the background it is kept current for the switch and for the Save & Submit comparison.
  const on = !!prediction && !prediction.held;

  // What the user changed: a filled answer that is not the prediction, or a predicted one that
  // is gone (the old option of a changed select).
  const changedIn = (a: AnswerState) =>
    Object.entries(a).filter(([k, v]) => (filled(v) ? !isPredicted(k) : on && wasPredicted(k))).map(([k]) => k);

  // The model keys travel with the first page: a housing row picked is one model, the height then
  // typed makes it another (DIMENSIONS holds the row's OD until the height is chosen), and the
  // field-by-field pages after the first are keyed on the model. They are not first-page
  // questions, so without this the prediction ran once on the half-chosen housing and never
  // again (Kevin's live test, 2026-10-18: Reliable Oilfield's own OT-SB28/12 orders were missed).
  const onPageOrModel = useMemo(() => (k: string) => onFirstPage(k) || MODEL_KEYS.has(questionOf(k)), [onFirstPage]);
  const page = useMemo(() => pageAnswers(answers, onPageOrModel), [answers, onPageOrModel]);
  const signature = useMemo(() => signatureOf(page), [page]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const changedKeys = useMemo(() => changedIn(page), [page, isPredicted, on]);
  const keys = useMemo(() => pageAnswers(answers, isKeyAnswer), [answers]);
  const keySignature = useMemo(() => signatureOf(keys), [keys]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const keyChanged = useMemo(() => changedIn(keys), [keys, isPredicted, on]);
  const guard = useMemo(() => pageAnswers(answers, isGuardAnswer), [answers]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mirrorHolds = useMemo(() => !!prediction?.mirrorRole && changedIn(guard).length === 0, [guard, isPredicted, on, prediction?.mirrorRole]);

  const frozen = useRef(false);
  const doneFor = useRef<string>("");
  const keyDoneFor = useRef<string>("");
  const inFlight = useRef<Promise<void>>(Promise.resolve());
  // The latest values for callbacks that outlive a render.
  const live = useRef({ page, signature, changedKeys, keys, keySignature, keyChanged, on, mirrorHolds, chosen });
  live.current = { page, signature, changedKeys, keys, keySignature, keyChanged, on, mirrorHolds, chosen };

  const refresh = (pageA: AnswerState, changed: string[], leaveAlone: (k: string) => boolean, userEdits = false) => {
    inFlight.current = (async () => {
      const res = await fetchRefinePrediction(product, getLaunchContext().customerId, pageA, changed);
      if (res) await refinePrediction(toInput(res), toSource(res), leaveAlone, { userEdits });
    })().catch((e) => console.warn("[cpq2] prediction refresh failed", e));
    return inFlight.current;
  };

  // Page-2 refresh (the first page as it stands; never the first page itself).
  const runPage2 = (): Promise<void> => {
    const { page: pg, signature: sig, changedKeys: changed } = live.current;
    if (doneFor.current === sig) return inFlight.current;
    doneFor.current = sig;
    // FIELD BY FIELD (util/fieldwise.ts) when the launch was: section by section from this
    // customer's same model and housing, everybody's, or an exact repeat -- set as they are.
    const fw = currentFieldwise();
    if (fw && fw.product === product && prediction?.launchSource.kind === "fieldwise") {
      inFlight.current = (async () => {
        const c = predictLaterPages(fw, answersLive.current);
        if (!Object.keys(c.values).length) return;
        const d = describeFieldwise(fw);
        await refinePrediction(
          { answers: toAnswers(c.values, fw.optionsOf), verbatim: true },
          { id: `F2`, name: d.name, customerName: null, createdAt: d.createdAt, basis: d.basis, kind: "fieldwise" },
          (k) => onFirstPage(k) || frozen.current,
          { userEdits: true, provenance: c.provenance }
        );
      })().catch((e) => console.warn("[cpq2] field-by-field refresh failed", e));
      return inFlight.current;
    }
    return refresh(pg, changed, (k) => onFirstPage(k) || frozen.current, true);
  };
  const page2Needed = () =>
    !single && !frozen.current && live.current.on && !live.current.mirrorHolds && live.current.chosen;

  // Key refresh: a key answer changed -> re-predict the rest from the best match on the keys.
  // Not under a field-by-field launch (util/fieldwise.ts): its first page was predicted answer by
  // answer and measured without this step, and an order type it did not predict (a customer with
  // no cpq2 configurations keeps the form's default) is not the user changing it. Running the old
  // whole-configuration re-pick here replaced Reliable Oilfield's own page 1 with another
  // customer's job (Kevin's live test, 2026-10-18).
  const fieldwise = prediction?.launchSource.kind === "fieldwise";
  useEffect(() => {
    if (!on || fieldwise || keyChanged.length === 0 || keyDoneFor.current === keySignature) return;
    const t = window.setTimeout(() => {
      const { keys: k, keySignature: sig, keyChanged: changed } = live.current;
      keyDoneFor.current = sig;
      // The key answer itself is the user's, and only what a launch predicts follows: the first
      // page minus the housing (the pages after it wait for the housing).
      void refresh(k, changed, (key) => isKeyAnswer(key) || !predictable(key));
    }, KEY_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keySignature, keyChanged.length, on, fieldwise]);

  // Page-2 refresh in the background while the first page is being answered.
  useEffect(() => {
    if (!page2Needed()) return;
    const t = window.setTimeout(() => void runPage2(), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, chosen, on, single]);

  // The customer's PO landed after the later pages were predicted: predict them again with it
  // (fieldwiseRun withPo), still only over answers nobody has touched, and never once frozen.
  useEffect(() => {
    const onPo = () => {
      if (!page2Needed()) return;
      doneFor.current = "";
      void runPage2();
    };
    window.addEventListener(PO_READY_EVENT, onPo);
    return () => window.removeEventListener(PO_READY_EVENT, onPo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // What Configurator awaits before leaving the first page: finish (or catch up) the refresh.
  useEffect(() => {
    leaveRef.current = async () => {
      await inFlight.current;
      if (page2Needed()) await runPage2();
    };
    return () => {
      leaveRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A later page is on screen: freeze. A jump that did not go through leaveRef (Save & Submit
  // sending the user to a flagged field) still gets its catch-up first.
  useEffect(() => {
    if (single || !activeTabId || activeTabId === firstTabId || frozen.current) return;
    const work = page2Needed() ? runPage2() : inFlight.current;
    void work.finally(() => {
      frozen.current = true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  return null;
}
